/**
 * เอกสารของฉัน — เอกสารรอเซ็น (สัญญาจ้าง ฯลฯ) + เอกสารของฉันทั้งหมด + อัปโหลดเพิ่ม.
 */
import { useState } from 'react';
import { AdminLayout } from '../components/layout/AdminLayout';
import { useToast } from '../components/ui/toast';
import { Modal } from '../components/ui/Modal';
import { SignaturePad } from '../components/ui/SignaturePad';
import { uploadFile, resolveFileUrl } from '../lib/api-client';
import {
  useMyPendingSign,
  useSignDocument,
  useOwnDocuments,
  useCreateOwnDocument,
  type DocumentRow,
  type EmployeeDocType,
} from '../hooks';
import { EMPLOYEE_DOC_TYPE_LABELS, EMPLOYEE_DOC_TYPES } from '@payroll/shared';
import { FileText, PenLine, Upload, FileSignature } from 'lucide-react';

export function MyDocuments() {
  const { data: pending, isLoading: pendingLoading } = useMyPendingSign();
  const { data: myDocs, isLoading: docsLoading } = useOwnDocuments();
  const createM = useCreateOwnDocument();
  const toast = useToast();

  const [signingDoc, setSigningDoc] = useState<(DocumentRow & { pdf_url: string }) | null>(null);
  const [docType, setDocType] = useState<EmployeeDocType>('national_id');
  const [uploading, setUploading] = useState(false);

  async function handleFile(file: File | null) {
    if (!file) return;
    setUploading(true);
    try {
      const path = await uploadFile(file, 'identity', file.name);
      await createM.mutateAsync({
        label: EMPLOYEE_DOC_TYPE_LABELS[docType],
        r2_path: path,
        mime_type: file.type || undefined,
        size_bytes: file.size,
        doc_type: docType,
      });
      toast.success('อัปโหลดเอกสารสำเร็จ');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'อัปโหลดไม่สำเร็จ');
    } finally {
      setUploading(false);
    }
  }

  return (
    <AdminLayout title="เอกสารของฉัน">
      {/* รอเซ็น */}
      <div className="mb-4">
        <div className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1">
          <FileSignature className="w-4 h-4" /> รอเซ็น
        </div>
        {pendingLoading ? (
          <p className="text-xs text-gray-400">กำลังโหลด…</p>
        ) : !pending || pending.length === 0 ? (
          <p className="text-xs text-gray-400 italic">ไม่มีเอกสารรอเซ็น</p>
        ) : (
          <div className="space-y-2">
            {pending.map((d) => (
              <div key={d.id} className="card p-3 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium truncate">{d.label}</div>
                  <div className="text-xs text-gray-400">อัปโหลดเมื่อ {new Date(d.uploaded_at).toLocaleDateString('th-TH')}</div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <a href={d.pdf_url} target="_blank" rel="noreferrer" className="btn-secondary text-xs px-3 py-1.5">
                    ดูเอกสาร
                  </a>
                  <button onClick={() => setSigningDoc(d)} className="btn-cta text-xs px-3 py-1.5">
                    <PenLine className="w-3.5 h-3.5" /> เซ็นเอกสาร
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* อัปโหลดเอกสารใหม่ */}
      <div className="card p-3 space-y-2 mb-4">
        <div className="text-xs uppercase tracking-wider text-gray-500 flex items-center gap-1">
          <Upload className="w-3.5 h-3.5" /> อัปโหลดเอกสารของฉัน
        </div>
        <select value={docType} onChange={(e) => setDocType(e.target.value as EmployeeDocType)} className="input w-full">
          {EMPLOYEE_DOC_TYPES.map((t) => (
            <option key={t} value={t}>{EMPLOYEE_DOC_TYPE_LABELS[t]}</option>
          ))}
        </select>
        <input
          type="file"
          accept="image/*,application/pdf"
          onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
          disabled={uploading}
          className="text-sm"
        />
      </div>

      {/* เอกสารของฉัน */}
      <div>
        <div className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1">
          <FileText className="w-4 h-4" /> เอกสารของฉัน
        </div>
        {docsLoading ? (
          <p className="text-xs text-gray-400">กำลังโหลด…</p>
        ) : !myDocs || myDocs.length === 0 ? (
          <p className="text-xs text-gray-400 italic">ยังไม่มีเอกสาร</p>
        ) : (
          <div className="space-y-1">
            {myDocs.map((d) => (
              <div key={d.id} className="flex items-center justify-between border border-gray-100 rounded-lg px-3 py-2 text-sm">
                <div className="min-w-0">
                  <div className="truncate">
                    {d.doc_type ? EMPLOYEE_DOC_TYPE_LABELS[d.doc_type] : d.label}
                    {d.version ? <span className="text-gray-400"> v{d.version}</span> : null}
                  </div>
                  <div className="text-[10px] text-gray-400">{new Date(d.uploaded_at).toLocaleDateString('th-TH')}</div>
                </div>
                <a href={resolveFileUrl(d.url) ?? '#'} target="_blank" rel="noreferrer" className="text-xs text-brand-600 hover:underline flex-shrink-0">
                  ดู
                </a>
              </div>
            ))}
          </div>
        )}
      </div>

      {signingDoc && (
        <SignDocumentModal doc={signingDoc} onClose={() => setSigningDoc(null)} />
      )}
    </AdminLayout>
  );
}

function SignDocumentModal({ doc, onClose }: { doc: DocumentRow & { pdf_url: string }; onClose: () => void }) {
  const signM = useSignDocument();
  const toast = useToast();
  const [method, setMethod] = useState<'onscreen' | 'upload' | null>(null);
  const [uploading, setUploading] = useState(false);

  async function handleSigned(r2Path: string) {
    try {
      await signM.mutateAsync({ id: doc.id, method: 'onscreen', signature_r2_path: r2Path });
      toast.success('เซ็นเอกสารสำเร็จ');
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'เซ็นเอกสารไม่สำเร็จ');
    }
  }

  async function handleUploadFile(file: File | null) {
    if (!file) return;
    setUploading(true);
    try {
      const path = await uploadFile(file, 'contract', file.name);
      await signM.mutateAsync({ id: doc.id, method: 'upload', signed_r2_path: path });
      toast.success('อัปโหลดเอกสารที่เซ็นแล้วสำเร็จ');
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'อัปโหลดไม่สำเร็จ');
    } finally {
      setUploading(false);
    }
  }

  return (
    <Modal open onClose={onClose} title={`เซ็นเอกสาร — ${doc.label}`}>
      {!method ? (
        <div className="space-y-2">
          <button onClick={() => setMethod('onscreen')} className="btn-cta w-full">
            <PenLine className="w-4 h-4" /> เซ็นบนจอ
          </button>
          <button onClick={() => setMethod('upload')} className="btn-secondary w-full">
            <Upload className="w-4 h-4" /> อัปโหลดรูปเอกสารที่เซ็นแล้ว
          </button>
        </div>
      ) : method === 'onscreen' ? (
        <SignaturePad onSigned={handleSigned} onCancel={() => setMethod(null)} />
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-gray-600">พิมพ์เอกสาร เซ็นด้วยมือ แล้วถ่ายรูป/สแกนอัปโหลดกลับมาที่นี่</p>
          <input
            type="file"
            accept="image/*,application/pdf"
            onChange={(e) => handleUploadFile(e.target.files?.[0] ?? null)}
            disabled={uploading}
            className="text-sm"
          />
          <button onClick={() => setMethod(null)} className="btn-secondary w-full">
            ย้อนกลับ
          </button>
        </div>
      )}
    </Modal>
  );
}
